import React, { useEffect, useState } from 'react';
import { adminEmployeeApi } from '../services/api';

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [managers, setManagers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    employeeId: '', username: '', password: '', firstName: '', lastName: '',
    email: '', jobTitle: '', role: 'EMPLOYEE', scheduledHours: 40, managerId: '',
  });

  useEffect(() => { loadEmployees(); }, [showInactive]);

  const loadEmployees = async () => {
    try {
      const res = await adminEmployeeApi.list(showInactive);
      if (res.data.success) {
        setEmployees(res.data.data || []);
        setManagers((res.data.data || []).filter(e => e.role === 'MANAGER' || e.role === 'ADMIN'));
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, managerId: form.managerId ? parseInt(form.managerId) : null };
      if (editId) {
        const { password, ...updateData } = payload;
        await adminEmployeeApi.update(editId, password ? payload : updateData);
      } else {
        await adminEmployeeApi.create(payload);
      }
      setShowForm(false);
      resetForm();
      loadEmployees();
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving employee');
    }
  };

  const resetForm = () => {
    setEditId(null);
    setForm({ employeeId: '', username: '', password: '', firstName: '', lastName: '', email: '', jobTitle: '', role: 'EMPLOYEE', scheduledHours: 40, managerId: '' });
  };

  const editEmployee = (emp) => {
    setForm({
      employeeId: emp.employeeId, username: emp.username, password: '',
      firstName: emp.firstName, lastName: emp.lastName || '',
      email: emp.email || '', jobTitle: emp.jobTitle || '',
      role: emp.role, scheduledHours: emp.scheduledHours || 40,
      managerId: emp.managerId || '',
    });
    setEditId(emp.id);
    setShowForm(true);
  };

  const deactivate = async (id) => {
    if (window.confirm('Deactivate this employee?')) {
      await adminEmployeeApi.deactivate(id);
      loadEmployees();
    }
  };

  const reactivate = async (id) => {
    if (window.confirm('Reactivate this employee?')) {
      await adminEmployeeApi.reactivate(id);
      loadEmployees();
    }
  };

  const roleBadge = (r) => {
    const map = { ADMIN: 'badge-danger', MANAGER: 'badge-warning', HR: 'badge-info', EMPLOYEE: 'badge-gray' };
    return map[r] || 'badge-gray';
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>Employees</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show Inactive
          </label>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>+ Add Employee</button>
        </div>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <h3>{editId ? 'Edit Employee' : 'New Employee'}</h3>
            <button className="btn btn-sm btn-outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Employee ID</label>
                  <input className="form-control" value={form.employeeId} onChange={e => setForm({ ...form, employeeId: e.target.value })} required disabled={!!editId} />
                </div>
                <div className="form-group">
                  <label>Username</label>
                  <input className="form-control" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} required disabled={!!editId} />
                </div>
                <div className="form-group">
                  <label>Password {editId && '(leave blank to keep)'}</label>
                  <input className="form-control" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editId} />
                </div>
                <div className="form-group">
                  <label>First Name</label>
                  <input className="form-control" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input className="form-control" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-control" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Job Title</label>
                  <input className="form-control" value={form.jobTitle} onChange={e => setForm({ ...form, jobTitle: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select className="form-control" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                    <option value="EMPLOYEE">Employee</option>
                    <option value="MANAGER">Manager</option>
                    <option value="HR">HR</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Scheduled Hours</label>
                  <input className="form-control" type="number" value={form.scheduledHours} onChange={e => setForm({ ...form, scheduledHours: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Manager</label>
                  <select className="form-control" value={form.managerId} onChange={e => setForm({ ...form, managerId: e.target.value })}>
                    <option value="">No Manager</option>
                    {managers.map(m => (
                      <option key={m.id} value={m.id}>{m.firstName} {m.lastName} ({m.role})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button type="submit" className="btn btn-primary">{editId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Job Title</th>
                <th>Role</th>
                <th>Manager</th>
                <th>Team</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr><td colSpan="10" className="empty-state">No employees found</td></tr>
              ) : employees.map(emp => (
                <tr key={emp.id} style={{ opacity: emp.active === false ? 0.5 : 1 }}>
                  <td><strong>{emp.employeeId}</strong></td>
                  <td>{emp.firstName} {emp.lastName}</td>
                  <td>{emp.username}</td>
                  <td>{emp.email || '—'}</td>
                  <td>{emp.jobTitle || '—'}</td>
                  <td><span className={`badge ${roleBadge(emp.role)}`}>{emp.role}</span></td>
                  <td>{emp.managerName || '—'}</td>
                  <td>{emp.teamName || '—'}</td>
                  <td><span className={`badge ${emp.active !== false ? 'badge-success' : 'badge-danger'}`}>{emp.active !== false ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => editEmployee(emp)}>Edit</button>
                      {emp.active !== false ? (
                        <button className="btn btn-sm btn-danger" onClick={() => deactivate(emp.id)}>Deactivate</button>
                      ) : (
                        <button className="btn btn-sm btn-success" onClick={() => reactivate(emp.id)}>Reactivate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
